import { useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  CreateBusinessPartnerRequestDto,
  findContactsByCreatedByIdQueryKey,
  FindContactsByCreatedByIdResponseDto,
  organizationRequestDtoCountryEnum,
  OrganizationRequestDtoCountryEnumKey,
  UpdateBusinessPartnerRequestDto,
  useUpdateBusinessPartner,
} from "~/gen/api"
import { SupplierFormValues } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/columns"
import { useCreateContactMutation } from "~/pages/SupplierPortal/Contacts/hooks/useCreateContactMutation"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

type UseContactFormProps = {
  initialValues?: SupplierFormValues
  onContactCreated?: (contactId: string) => void
  organizationId: string
}
export const contactToSupplierMapping = (
  contact: FindContactsByCreatedByIdResponseDto
): SupplierFormValues => {
  return {
    contactId: contact.id,
    supplierId: contact.user.id.toString(),
    name: contact.user.name,
    email: contact.user.email,
    preferedLangageIso6391: contact.user.preferedLangageIso6391,
    organization: {
      businessRelationship: contact.user.organization.businessRelationship,
      name: contact.user.organization.name,
      street: contact.user.organization.street ?? "",
      city: contact.user.organization.city ?? "",
      postalCode: contact.user.organization.postalCode ?? "",
      country: contact.user.organization
        .country as OrganizationRequestDtoCountryEnumKey,
      registrationId: contact.user.organization.registrationId,
      coordinates: contact.user.organization.coordinates ?? null,
    },
  }
}

const defaultContact: SupplierFormValues = {
  contactId: "",
  supplierId: "",
  name: "",
  email: "",
  preferedLangageIso6391: "EN",
  organization: {
    businessRelationship: {
      isActive: true,
      supplierCode: null,
      type: null,
    },
    name: "",
    street: "",
    city: "",
    postalCode: "",
    country: organizationRequestDtoCountryEnum.FR,
    registrationId: [],
    coordinates: null,
  },
}

const useContactForm = ({
  initialValues,
  onContactCreated,
  organizationId,
}: UseContactFormProps) => {
  const { t } = useTranslation("traceability")

  const [contact, setContact] = useState<SupplierFormValues>(
    initialValues ?? defaultContact
  )
  const contactId =
    "contactId" in contact && contact.contactId !== "" ? contact.contactId : ""

  const { mutate: createContactMutate, isPending: isCreating } =
    useCreateContactMutation(organizationId, {
      onSuccess: (data) => {
        onContactCreated?.(data)
        setContact((oldContact) => ({
          ...oldContact,
          contactId: data.toString(),
        }))
      },
    })

  const createContact = (values: CreateBusinessPartnerRequestDto) => {
    createContactMutate(values)
  }

  const { mutate: updateContactMutate, isPending: isUpdating } =
    useUpdateBusinessPartner({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: findContactsByCreatedByIdQueryKey(organizationId),
          })
          await queryClient.invalidateQueries({
            queryKey: ["tags"],
          })
          showSuccessNotification({
            message: t("contactModal.messages.update.success", {
              email: contact.email,
            }),
            title: t("contactModal.messages.update.success", {
              email: contact.email,
            }),
          })
        },
      },
    })

  const updateContact = (values: UpdateBusinessPartnerRequestDto) => {
    updateContactMutate({
      id: contactId,
      data: values,
    })
  }

  const handleContact = (values?: SupplierFormValues) => {
    if ("contactId" in contact && contact.contactId !== "") {
      updateContact((values as UpdateBusinessPartnerRequestDto) ?? contact)
    } else {
      createContact(values ?? contact)
    }
  }

  const reset = () => {
    setContact(defaultContact)
  }

  return {
    contact,
    setContact,
    contactId,
    handleContact,
    reset,
    isCreating,
    isUpdating,
  }
}

export default useContactForm
